import { Box, Container, Grid2 } from "@mui/material";
import { useRef } from "react";
import { Link } from "react-router-dom";

export const ProactiveSeverityLitigationIntelligenceDetail = () => {
  const productSectionRef = useRef(null);
  const valueSectionRef = useRef(null);
  const financialSectionRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    const sectionTop =
      sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: sectionTop - 110, behavior: "smooth" });
  };

  return (
  <Container maxWidth="lg" className="pt-6">
      <Box className="mb-8 md:mb-10 rounded-xl border border-[#021744] bg-[#021744]">
        <Box className="bg-[#021744] text-white rounded-xl p-6 md:p-10">
          <Box className="w-full flex flex-wrap justify-between items-start gap-4 mb-6">
            <span className="inline-flex text-xs px-3 py-1 rounded-full bg-[#42DDD1] border border-[#42DDD1] text-[#021744]">
              Use Case Spotlight
            </span>
            <Box className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => scrollToSection(valueSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Value
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(financialSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Financial Impact
              </button>
              <button
                type="button"
                onClick={() => scrollToSection(productSectionRef)}
                className="text-xs px-3 py-1 rounded-full border border-white/40 bg-white/10 text-white hover:bg-[#42DDD1] hover:text-[#021744]"
              >
                Product
              </button>
            </Box>
          </Box>
          <h1 className="text-left text-[26px] md:text-[34px] tracking-normal font-medium mb-4">
            See Severity &amp; Litigation Risk Coming — And Act Before It Hits
          </h1>
          <p className="text-[#d6e6ff] leading-7 mb-4 text-left">
            In today’s environment of rising claim complexity—marked by early
            attorney involvement, nuclear verdicts, social inflation, and
            widening regional variance— litigation risk and severity can
            escalate quickly and unpredictably. Claims teams often lack the
            visibility to understand why outcomes vary across jurisdictions,
            claim types, or claimant profiles, resulting in inconsistent
            handling and avoidable cost spikes.
          </p>
          <p className="text-[#d6e6ff] leading-7 text-left">
            4SeeCharlee empowers insurers to shift from reactive litigation
            response to proactive risk management. Using NLP-based forecasting
            and advanced pattern recognition, Charlee analyzes millions of
            historical claims to detect top severity and litigation drivers by
            region, line of business, and claimant profile. These insights help
            claims leaders predict where legal escalation is most likely and act
            early—whether through triage, negotiation, or training—before the
            cost curve bends upward.
          </p>
        </Box>
      </Box>
      <Grid2 container spacing={2} className="mb-8 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Role</p>
            <p className="text-sm text-[#1a2f55]">
              Claims Executives, Managers, Supervisors, Adjusters
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">
              Job at Hand
            </p>
            <p className="text-sm text-[#1a2f55]">
              Improve litigation strategy and reduce severity exposure
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-5 h-full">
            <p className="text-center font-bold text-[#294b78] mb-2">Trigger</p>
            <p className="text-sm text-[#1a2f55]">
              High variance in claim outcomes across regions
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Opportunity
        </h3>
        <p className="text-[#42506a]">
          Predictive visibility into litigation and severity patterns enables
          earlier, smarter interventions and tailored team training.
        </p>
      </Box>
      <Grid2
        ref={productSectionRef}
        container
        spacing={2}
        className="mb-4 items-stretch"
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">Before Charlee</h4>
            <p className="text-[#42506a]">
              Litigation insights were only available post-closure; teams
              operated reactively and lacked visibility into root drivers of
              legal escalation.
            </p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 h-full">
            <h4 className="text-[#021744] font-medium mb-2">With Charlee</h4>
            <p className="text-[#42506a]">
              AI surfaces top 5 litigation and severity risk patterns across
              regions, claim types, and claimant profiles—powering proactive
              strategy, segmentation, and training.
            </p>
          </Box>
        </Grid2>
      </Grid2>
      <Grid2 container spacing={2} className="mb-4 items-stretch">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Product Used</p>
            <p>🔎 4SeeCharlee</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">LOBs</p>
            <p>All</p>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box className="rounded-xl border border-[#021744] bg-[#021744] text-white p-5 h-full">
            <p className="text-xs text-[#42DDD1] mb-2">Area</p>
            <Box className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Claims
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Severity
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-white/10">
                Litigation
              </span>
            </Box>
          </Box>
        </Grid2>
      </Grid2>
      <Box
        ref={valueSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Value Proposition
        </h3>
        <p className="text-[#42506a] text-left">
          Top exposure patterns support proactive strategy, segmentation, and
          targeted adjuster training.
        </p>
      </Box>
      <Box
        ref={financialSectionRef}
        className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4"
      >
        <h3 className="text-[22px] text-[#021744] font-medium mb-3">
          Financial Impact
        </h3>
        <p className="text-[#42506a] mb-2 text-left">
          Delayed visibility into litigation and severity drivers leads to:
        </p>
        <ul className="text-[#42506a] space-y-2 mb-3 text-left">
          <li>
            • Higher legal spend, due to late involvement of defense counsel and
            prolonged disputes
          </li>
          <li>
            • Inflated indemnity payments, from missed negotiation or early
            resolution windows
          </li>
          <li>
            • Inefficient adjuster training, with no focus on exposure-based
            risk trends{" "}
          </li>
        </ul>
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">
            With Charlee, insurers benefit from:
          </p>
          <ul className="text-[#42506a] space-y-2 text-left">
            <li>
              • $12K–$25K savings per litigated claim via faster resolution and
              smarter allocation
            </li>
            <li>
              • 17% reduction in regional litigation rates in pilot programs
            </li>
            <li>
              • Region-specific training insights to better prepare claims teams
            </li>
            <li>• Improved alignment between claims and litigation strategy</li>
          </ul>
        </Box>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <h3 className="text-[28px] md:text-[32px] text-[#021744] font-bold mb-4">
          Results
        </h3>
        <Grid2 container spacing={2} className="items-stretch">
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                🚀 17%
              </p>
              <p className="text-[#42506a]">
                regional litigation rates down in pilot markets
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p>✅</p>
              <p className="text-[#42506a]">
                adjuster training tailored to top exposure patterns
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p>💬</p>
              <p className="text-[#42506a]">
                “For the first time, we’re managing risk with foresight.”
              </p>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Box className="rounded-lg bg-[#EDF3F8] p-4 h-full">
              <p className="text-xl md:text-2xl font-bold text-[#021744] whitespace-nowrap">
                ⏱️ 3 weeks
              </p>
              <p className="text-[#42506a]">Actionable insights delivered</p>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 mb-4">
        <Box className="rounded-lg border border-[#E6EAF2] bg-[#EDF3F8] p-4 text-left">
          <p className="text-[#021744] font-bold mb-2">Why It Matters</p>
          <p className="text-[#42506a]">
            Predictive risk detection improves litigation outcomes, reduces
            costs, and strengthens claims team performance through smarter
            strategy and training.
          </p>
        </Box>
      </Box>
      <Box className="pt-8 text-center">
        <Link
          to="/blogs/usecases"
          className="inline-flex items-center gap-2 text-[#021744] border border-[#021744] bg-white rounded-full px-6 py-3 hover:bg-[#021744] hover:text-white"
        >
          Back to Use Cases
        </Link>
      </Box>
    </Container>
  );
};
